import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Grid,
  CircularProgress,
  Container,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { makeStyles, styled } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useHealthIntervals, useTelemetry } from 'hooks';
import TelemetryTableCell from './TelemetryTableCell';
import TelemetryTableTitleCell from './TableCellTitle';
import DepthCell from './DepthCell';
import SurfaceTableCell from './SurfaceTableCell';

import { DashboardHelpers, ReadingsHelpers } from 'utils';

const NoPaddingTableCell = styled(TableCell)({
  padding: 0,
  border: 'none',
  textAlign: 'center',
});

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
  gradient: {
    background: 'linear-gradient( #e1dad5, #cec4bb)',
  },
  tableWrapper: {
    width: '100%',
    [theme.breakpoints.down('md')]: {
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  },
}));

const StatsTable = ({
  convertPPM,
  convertTemp,
  convertUnits,
  selectedField,
  selectedDevice,
  fieldTelemetry,
  deviceTelemetry,
}) => {
  const { t } = useTranslation();
  const { getStatusForValue, getIntervalForReadingAtDepth, getSuggestionForInterval } = useHealthIntervals();
  const { isFetching } = useTelemetry();
  const classes = useStyles();
  const [isMobile, setIsMobile] = useState(false);

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  if (isFetching === true) {
    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        justifyItems="center"
      >
        <CircularProgress />
        <Typography variant="h4" style={{ paddingLeft: 15 }}>
          {t('loading')}
        </Typography>
      </Grid>
    );
  }

  let maxDepth = 0;
  let titles = [];
  let titleCells = [<TelemetryTableTitleCell key={Math.random() * 10000} title={t('depth')} />];
  let telemetryUnitCells = [<NoPaddingTableCell key={Math.random() * 10000} />];
  let upperTitles = [];
  let data = [];
  let subterraneanData = {};
  let airData = {};

  const getInterval = (telemetryType, depth = undefined) => {
    if (depth) {
      const interval = getIntervalForReadingAtDepth(telemetryType, depth);

      return interval;
    }

    return {};
  };

  const telemetry =
    selectedField?.id && (selectedDevice?.id === DashboardHelpers.StatusCodes.NoDeviceSelected || !selectedDevice?.id)
      ? fieldTelemetry[selectedField.id]
      : deviceTelemetry[selectedDevice.hw_address];

  _.forOwn(telemetry, (v) => {
    data.push(v);
  });

  if (!isFetching && data.length === 0) {
    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        justifyItems="center"
      >
        <Typography variant="subtitle1" style={{ paddingLeft: 15 }}>
          {t('no_data')}
        </Typography>
      </Grid>
    );
  }

  if (data.length > 0) {
    const latest = data[data.length - 1];

    _.forEach(latest.data, (v, k) => {
      _.forEach(v, (_, depth) => {
        if (depth > maxDepth) {
          maxDepth = depth;
        }
      });
      if (Object.keys(v).length > 0 && Object.keys(v).includes('0')) {
        upperTitles.push(k);
        if (airData[k] === undefined) {
          airData[k] = [];
        }
        airData[k].push(v['0'].value);
      } else {
        if (!titles.includes(k)) {
          titles.push(k);
          const convert = ReadingsHelpers.getNeedsConversion(k, convertPPM, convertTemp, convertUnits);
          const units = ReadingsHelpers.getUnitsForReading(k, convert);
          titleCells.push(<TelemetryTableTitleCell key={Math.random() * 10000} title={t(k)} type={units} />);
        }

        subterraneanData[k] = v;
      }
    });
  } else {
    titleCells = [];
  }
  const rows = [];
  for (let i = 1; i <= maxDepth; i++) {
    const cells = [<DepthCell key={Math.random() * 10000} value={i}></DepthCell>];
    for (let j = 0; j < titles.length; j++) {
      const title = titles[j];
      const telemetryTypeBucket = subterraneanData[title];

      if (telemetryTypeBucket !== undefined) {
        const value = telemetryTypeBucket[`${i}`];

        const nextValue = telemetryTypeBucket[`${i + 1}`];

        const nextEmpty = nextValue === undefined;
        const thisEmpty = value === undefined;

        const hasAny = !!_.find(telemetryTypeBucket, (v) => !!v.value || !!v.error);

        if (!thisEmpty) {
          cells.push(
            <TelemetryTableCell
              key={Math.random() * 10000}
              value={value.value}
              uncertainty={value.uncertainty}
              error={value.error}
              final={nextEmpty}
              telemetryType={title}
              getInterval={getInterval}
              getSuggestion={getSuggestionForInterval}
              getHealthStatus={getStatusForValue}
              convertPPM={convertPPM}
              convertTemp={convertTemp}
              convertUnits={convertUnits}
              depth={i}
            />
          );
        } else if ((thisEmpty && !nextEmpty) || (hasAny && maxDepth != i)) {
          cells.push(<TelemetryTableCell key={Math.random() * 10000} showLineForEmpty />);
        } else {
          cells.push(<TelemetryTableCell key={Math.random() * 10000} final />);
        }
      }
    }
    rows.push(<TableRow key={Math.random() * 10000}>{cells}</TableRow>);
  }

  const surfaceCells = [];
  const airDataKeys = Object.keys(airData);
  _.forEach(airDataKeys, (airDataKey) => {
    surfaceCells.push(
      <SurfaceTableCell
        key={Math.random() * 10000}
        value={airData[airDataKey]}
        surfaceDataType={airDataKey}
        convertTemp={convertTemp}
        convertPPM={convertPPM}
        convertUnits={convertUnits}
      />
    );
  });

  return (
    <div className={classes.tableWrapper}>
      {isMobile ? (
        <Container>
          <Table key={Math.random() * 10000}>
            <TableBody key={Math.random() * 10000}>
              <TableRow>{surfaceCells}</TableRow>
            </TableBody>
          </Table>
          <Table key={Math.random() * 10000}>
            <TableHead key={Math.random() * 10000}>
              <TableRow key={Math.random() * 10000}>{titleCells}</TableRow>
              <TableRow key={Math.random() * 10000}>{telemetryUnitCells}</TableRow>
            </TableHead>
            <TableBody className={classes.gradient} key={Math.random() * 10000}>
              {rows}
            </TableBody>
          </Table>
        </Container>
      ) : (
        <div>
          <Table key={Math.random() * 10000}>
            <TableBody key={Math.random() * 10000}>
              <TableRow>{surfaceCells}</TableRow>
            </TableBody>
          </Table>
          <Table key={Math.random() * 10000}>
            <TableHead key={Math.random() * 10000}>
              <TableRow key={Math.random() * 10000}>{titleCells}</TableRow>
              <TableRow key={Math.random() * 10000}>{telemetryUnitCells}</TableRow>
            </TableHead>
            <TableBody className={classes.gradient} key={Math.random() * 10000}>
              {rows}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

StatsTable.propTypes = {
  tableTitles: PropTypes.array,
  getIntervals: PropTypes.func,
  getStatusForValue: PropTypes.func,
  getSuggestionForInterval: PropTypes.func,
  activeCropId: PropTypes.string,
  convertPPM: PropTypes.bool,
  convertTemp: PropTypes.bool,
  selectedField: PropTypes.object,
  selectedDevice: PropTypes.object,
  fieldTelemetry: PropTypes.object,
  deviceTelemetry: PropTypes.object,
  convertUnits: PropTypes.bool,
};

StatsTable.defaultProps = {
  tableTitles: [],
  getIntervals: () => {},
  getStatusForValue: () => {},
  getSuggestionForInterval: () => {},
  activeCropId: '',
  convertPPM: false,
  convertTemp: false,
  convertUnits: false,
  selectedField: {},
  selectedDevice: {},
  fieldTelemetry: {},
  deviceTelemetry: {},
};

export default StatsTable;
